/**
 * Recursive function for executing query on single feature
 * @param {object} data olFeature.values_ from map
 * @param {object} query Query constructed by user, in format described above
 * @param {object[]} queryData Queryable layers from state
 * @param {string} dataSource DisplayName of layer being queried
 * @returns {boolean}
 */
export const executeQuery = (data, query, queryData, dataSource) => {
    let isTrue;
    const q = query.conditions;

    if (query.logicOperator === "AND") {
        for (let i = 0; i < q.length; i++) {
            if (q[i].logicOperator !== undefined) {
                isTrue = executeQuery(data, q[i], queryData, dataSource);
            } else {
                const dataType = determineFieldType(queryData, q[i].field, dataSource);
                const fieldName = findName(queryData, q[i].field, dataSource);
                if (dataType === "String") {
                    isTrue = evaluateStrings(data[fieldName], q[i]);
                } else if (dataType === "Date") {
                    const dateNum = new Date(data[fieldName]);
                    isTrue = evaluateNumbers(dateNum, q[i]);
                } else {
                    isTrue = evaluateNumbers(data[fieldName], q[i]);
                }
                if (isTrue === false) return (isTrue = isTrue);
            }
        }
    } else if (query.logicOperator === "OR") {
        const q = query.conditions;
        for (let i = 0; i < q.length; i++) {
            if (q[i].logicOperator !== undefined) {
                executeQuery(data, q[i], queryData, dataSource);
            } else {
                const dataType = determineFieldType(queryData, q[i].field, dataSource);
                const fieldName = findName(queryData, q[i].field, dataSource);
                if (dataType === "String") {
                    isTrue = evaluateStrings(data[fieldName], q[i]);
                } else if (dataType === "Date") {
                    const dateNum = Date.parse(data[fieldName]);
                    isTrue = evaluateNumbers(dateNum, q[i]);
                } else {
                    isTrue = evaluateNumbers(data[fieldName], q[i]);
                }
                if (isTrue) return (isTrue = isTrue);
            }
        }
    }
    return isTrue;
};

/**
 * Test for field with string data type
 * @param {string} data Value of field
 * @param {import("./QueryFilter").Clause} q Single clause from query
 * @returns {boolean}
 */
const evaluateStrings = (data, q) => {
    let isTrue;
    if (q.value === "" || q.value === undefined || data === null) return (isTrue = false);
    switch (q.operator) {
        case "contains":
            isTrue = testContains(data, q);
            break;
        case "does not contain":
            isTrue = !testContains(data, q);
            break;
        case "=":
            isTrue = testStringEquality(data, q);
            break;
        case "!=":
            isTrue = !testStringEquality(data, q);
            break;
        case "starts with":
            isTrue = doesStartWith(data, q);
            break;
        case "ends with":
            isTrue = doesEndWith(data, q);
            break;
    }
    return isTrue;
};

/**
 * @param {string} data Value of field
 * @param {import("./QueryFilter").Clause} q Single clause from query
 * @returns {boolean}
 */
const testContains = (data, q) => {
    const doesContain = data.toLowerCase().includes(q.value.toLowerCase());
    return doesContain;
};

/**
 * @param {string} data Value of field
 * @param {import("./QueryFilter").Clause} q Single clause from query
 * @returns {boolean}
 */
const testStringEquality = (data, q) => {
    const isEqual = data.toLowerCase() === q.value.toLowerCase();
    return isEqual;
};

/**
 * @param {string} data Value of field
 * @param {import("./QueryFilter").Clause} q Single clause from query
 * @returns {boolean}
 */
const doesStartWith = (data, q) => {
    const startsWith =
        data.substr(0, q.value.length).toLowerCase() === q.value.toLowerCase();
    return startsWith;
};

/**
 * @param {string} data Value of field
 * @param {import("./QueryFilter").Clause} q Single clause from query
 * @returns {boolean}
 */
const doesEndWith = (data, q) => {
    const endsWith =
        data.substr(-q.value.length, q.value.length).toLowerCase() ===
        q.value.toLowerCase();
    return endsWith;
};

/**
 * Test for field with number data type
 * @param {number} data Value of field
 * @param {import("./QueryFilter").Clause} q Single clause from query
 * @returns {boolean}
 */
const evaluateNumbers = (data, q) => {
    let isTrue;
    if (q.value === null || data === null) return (isTrue = false);
    switch (q.operator) {
        case "=":
            isTrue = data === q.value;
            break;
        case "<":
            isTrue = data < q.value;
            break;
        case ">":
            isTrue = data > q.value;
            break;
        case "<=":
            isTrue = data <= q.value;
            break;
        case ">=":
            isTrue = data >= q.value;
            break;
        case "!=":
            isTrue = !data === q.value;
            break;
    }
    return isTrue;
};

// returns string
export const determineFieldType = (queryData, inputField, dataSource) => {
    // determine field type
    const queryDataSet = queryData.find((d) => d.DisplayName === dataSource);

    let field;
    if (queryDataSet.Fields === undefined) {
        return;
    } else if (Array.isArray(queryDataSet.Fields.Field) !== true) {
        field = queryDataSet.Fields.Field;
    } else {
        field = queryDataSet.Fields.Field.find((f) => f.DisplayName === inputField);
    }
    const fieldType = field?.Type ?? getDomainType(field) ?? "String";
    return fieldType;
};

export const findDomain = (queryData, inputField, dataSource) => {
    const queryDataSet = queryData.find((d) => d.DisplayName === dataSource);
    let field;
    if (queryDataSet.Fields === undefined) {
        return;
    } else if (Array.isArray(queryDataSet.Fields.Field) !== true) {
        field = queryDataSet.Fields.Field;
    } else {
        field = queryDataSet.Fields.Field.find((f) => f.DisplayName === inputField);
    }
    if (field?.Domains === undefined) return undefined;
    const domains = field.Domains;
    if (Array.isArray(domains.Domain) !== true) return [domains.Domain];
    return domains.Domain;
};

const getDomainType = (field) => {
    if (field?.Domains === undefined) return undefined;
    const domains = field.Domains;
    let value;
    let type;
    if (Array.isArray(domains.Domain) !== true) {
        value = domains.Domain.Code;
    } else {
        value = domains.Domain[0].Code;
    }
    if (typeof value === "string") type = "String";
    if (typeof value === "number") {
        type = Number.isInteger(value) ? "Int" : "Double";
    }
    return type;
};

/**
 * Match field being queired to field name from map data
 * @param {object[]} queryData All queryable layers from state
 * @param {string} inputField Display name of field being queried
 * @param {string} dataSource Name of layer being queried
 * @returns {string} Field name to use when querying data from map
 */
export const findName = (queryData, inputField, dataSource) => {
    const queryDataSet = queryData.find((d) => d.DisplayName === dataSource);
    let field;
    if (!queryDataSet.Fields) {
        return;
    } else if (Array.isArray(queryDataSet.Fields.Field) !== true) {
        field = queryDataSet.Fields.Field;
    } else {
        field = queryDataSet.Fields.Field.find((f) => f.DisplayName === inputField);
    }
    return field.Name;
};
